import React, { useRef, useState, useLayoutEffect, useMemo } from 'react';
import { Moon, Dumbbell, Activity, Utensils, Pill, CheckSquare, Sparkles } from 'lucide-react';
import { EVENT_TYPES } from '@/shared/constants/event-types.v4';
import { TimelineDraftBlock } from './TimelineDraftBlock';
import { TimelineSidebar } from './timeline/TimelineSidebar';
import { TimelineGrid } from './timeline/TimelineGrid';
import { TimelineActivity } from './timeline/TimelineActivity';
import { getPosition, getGranularity, getPositionedActivities } from '@/shared/logic/timeline-logic';
import { useTimelineZoom } from '../hooks/useTimelineZoom';
import { Event } from '@/types/zod.schemas';
import { Box, Typography } from '@/shared/design-system/primitives';
import { toLogicDate } from '@/utils/date-utils';

// Modular Stores (v4)
import { useComposerStore } from '@stores/v4/useComposerStore';
import { useSupplementStore, SupplementGhost } from '../../../stores/v4/useSupplementStore';
import { useMetabolicStore } from '../../../stores/v4/useMetabolicStore';

interface MasterTimelineViewProps {
    events: Event[];
    selectedDate: Date;
    onSelectEvent: (event: Event) => void;
}

/**
 * MasterTimelineView: Tactical canvas for temporal event visualization.
 * HARDENED: Robust 'Now' line visibility and scroll performance optimizations.
 */
export const MasterTimelineView: React.FC<MasterTimelineViewProps> = ({
    events,
    selectedDate,
    onSelectEvent
}) => {
    const timelineRef = useRef<HTMLDivElement>(null);
    
    // Hardened today check
    const isToday = useMemo(() => {
        const d1 = toLogicDate(selectedDate);
        const d2 = toLogicDate(new Date());
        return d1 === d2;
    }, [selectedDate]);

    const { openComposer, saveComposedEvent, updateComposedEvent } = useComposerStore();
    const { getDailyProjections } = useSupplementStore();
    const { dailySnapshot } = useMetabolicStore();

    // Pivot zoom around current time if viewing today
    const nowDecimal = useMemo(() => {
        const now = new Date();
        return now.getHours() + now.getMinutes() / 60;
    }, [isToday]);

    const { 
        hourHeight, setHourHeight, isZooming, handleZoomStart 
    } = useTimelineZoom(timelineRef, 30, {
        minHeight: 20,
        maxHeight: 600,
        sensitivity: 2.5,
        anchorTime: isToday ? nowDecimal : undefined
    });

    const [draftEvent, setDraftEvent] = useState<{ start: Date; end: Date } | null>(null); 

    useLayoutEffect(() => {
        if (timelineRef.current) {
            const headerHeight = 54;
            const calendarHeight = (document.querySelector('.calendar-container') as HTMLElement)?.offsetHeight || 280;
            const available = window.innerHeight - headerHeight - calendarHeight - 60;
            const minFit = Math.max(20, available / 24);
            setHourHeight(minFit);
        }
    }, [selectedDate, setHourHeight]);

    const projections = useMemo(() => 
        getDailyProjections(selectedDate, events, dailySnapshot), 
    [selectedDate, events, dailySnapshot, getDailyProjections]);

    const getIcon = (type: string) => {
        switch(type) {
            case EVENT_TYPES.PERFORMANCE: return Dumbbell;
            case EVENT_TYPES.MOBILITY: return Activity;
            case EVENT_TYPES.RECOVERY: return Moon;
            case EVENT_TYPES.NUTRITION: return Utensils;
            case EVENT_TYPES.SUPPLEMENTATION: return Pill;
            default: return CheckSquare;
        }
    };

    const handleBackgroundClick = (e: React.MouseEvent | React.TouchEvent) => {
        if (isZooming || !timelineRef.current) return;
        if ('touches' in e && e.touches.length > 1) return;
        const target = e.target as HTMLElement;
        if (target.dataset.isGrid !== "true" && target !== timelineRef.current) {
            if (!target.closest('.draft-block')) setDraftEvent(null);
            return;
        }
        const rect = timelineRef.current.getBoundingClientRect();
        const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
        const y = clientY - rect.top + timelineRef.current.scrollTop;
        const totalMinutes = y / (hourHeight / 60);
        const hour = Math.floor(totalMinutes / 60);
        const interval = getGranularity(hourHeight);
        const minute = Math.floor((totalMinutes % 60) / interval) * interval; 
        const start = new Date(selectedDate);
        start.setHours(hour, minute, 0, 0);
        const end = new Date(start.getTime() + interval * 60000);
        setDraftEvent({ start, end });
    };

    const positionedActivities = getPositionedActivities(events, selectedDate, hourHeight);
    const labelFontSize = Math.max(6, Math.min(11, hourHeight / 5.5));

    const nowLineY = useMemo(() => getPosition(new Date(), selectedDate, hourHeight), [hourHeight, selectedDate]);

    return (
        <div
            ref={timelineRef}
            onClick={handleBackgroundClick}
            onTouchStart={handleZoomStart}
            style={{
                height: '100%', overflowY: 'auto', position: 'relative',
                backgroundColor: '#050505', border: '1px solid #222', borderRadius: '12px',
                cursor: 'cell', touchAction: isZooming ? 'none' : 'pan-y', WebkitOverflowScrolling: 'touch',
                overflowAnchor: 'none'
            }}
        >
            <TimelineSidebar hours={Array.from({ length: 24 }, (_, i) => i)} hourHeight={hourHeight} labelFontSize={labelFontSize} isZooming={isZooming} onZoomStart={handleZoomStart} />
            <TimelineGrid hours={Array.from({ length: 24 }, (_, i) => i)} hourHeight={hourHeight} opacity30m={Math.max(0, Math.min(0.15, (hourHeight - 50) / 50))} opacity15m={Math.max(0, Math.min(0.08, (hourHeight - 120) / 100))} />

            <div style={{ position: 'absolute', top: 0, left: '60px', right: '10px', bottom: 0, pointerEvents: 'none' }}>
                {/* 1. RENDER GHOST SUPPLEMENTS */}
                {projections.filter(p => !p.isCrystallized).map((ghost) => (
                    <div 
                        key={ghost.window}
                        onClick={(e) => { e.stopPropagation(); openComposer(selectedDate, { start: ghost.scheduledTime, type: 'SUPPLEMENTATION', window: ghost.window }); }}
                        style={{ 
                            position: 'absolute', 
                            top: 0,
                            transform: `translateY(${getPosition(ghost.scheduledTime, selectedDate, hourHeight)}px)`, 
                            left: 0, right: 0, height: `${Math.max(20, hourHeight/2)}px`,
                            backgroundColor: 'rgba(168, 85, 247, 0.03)',
                            borderWidth: 1, borderStyle: 'dashed', borderColor: 'rgba(168, 85, 247, 0.3)',
                            borderRadius: 8, padding: '4px 8px', flexDirection: 'row', alignItems: 'center', gap: 6,
                            pointerEvents: 'auto', cursor: 'pointer'
                        } as any}
                    >
                        <Sparkles size={10} color="#a855f7" opacity={0.6} />
                        <Typography style={{ fontSize: 7, fontWeight: '900', color: '#666', letterSpacing: 0.5 }}>SUGGESTED: {ghost.label}</Typography>
                    </div>
                ))}

                {draftEvent && (
                    <TimelineDraftBlock 
                        start={draftEvent.start} end={draftEvent.end} selectedDate={selectedDate} hourHeight={hourHeight}
                        onUpdate={(upd: any) => setDraftEvent(prev => prev ? ({ ...prev, ...upd }) : null)}
                        onCancel={() => setDraftEvent(null)}
                        onConfirm={async (title: string) => {
                            await openComposer(selectedDate, { start: draftEvent.start, end: draftEvent.end });
                            if (title) updateComposedEvent({ title });
                            await saveComposedEvent();
                            setDraftEvent(null);
                        }}
                        onExpand={(title: string) => {
                            openComposer(selectedDate, { start: draftEvent.start, end: draftEvent.end });
                            if (title) updateComposedEvent({ title });
                            setDraftEvent(null);
                        }}
                    />
                )}

                {positionedActivities.map(act => (
                    <TimelineActivity key={act.id} act={act} isZooming={isZooming} labelFontSize={labelFontSize} icon={getIcon(act.type)} onSelect={onSelectEvent} />
                ))}
            </div>

            {isToday && (
                <div style={{ 
                    position: 'absolute', 
                    top: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                    transform: `translateY(${nowLineY}px)`, 
                    height: '1px', 
                    backgroundColor: '#ef4444', 
                    zIndex: 1000, 
                    pointerEvents: 'none',
                    display: 'block'
                }} />
            )}

            {isZooming && (
                <div style={{ position: 'absolute', top: '10px', right: '10px', backgroundColor: 'rgba(242, 155, 17, 0.9)', color: '#000', padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: '900', zIndex: 1000 }}>
                    SCALING TIMELINE
                </div>
            )}
        </div>
    );
};
